// frontend/src/features/onboarding/steps/WelcomeLocationStep.tsx
import React, { useState } from 'react'
import { ArrowRight } from 'lucide-react'

interface WelcomeLocationStepProps {
    eyebrow: string
    accentClass: string
    btnClass: string
    initialData?: { country: string; state: string; city: string }
    onNext: (data: { country: string; state: string; city: string }) => void
}

const REGIONS = [
    {
        key: 'us_east',
        label: 'US East',
        sub: 'NY · DC · Boston',
        country: 'United States', state: 'New York',
        // Simple map shape — eastern US silhouette
        path: 'M 20,8 L 38,6 L 42,10 L 40,18 L 36,24 L 30,28 L 22,26 L 16,20 L 14,14 Z',
        color: '#6600FF',
    },
    {
        key: 'us_west',
        label: 'US West',
        sub: 'SF · LA · Seattle',
        country: 'United States', state: 'California',
        path: 'M 8,6 L 18,4 L 20,10 L 18,20 L 14,28 L 8,26 L 4,18 L 4,12 Z',
        color: '#9966ff',
    },
    {
        key: 'us_central',
        label: 'US Central',
        sub: 'Chicago · Austin · Denver',
        country: 'United States', state: 'Illinois',
        path: 'M 12,6 L 28,6 L 30,14 L 26,22 L 18,24 L 10,20 L 8,12 Z',
        color: '#00c896',
    },
    {
        key: 'europe_west',
        label: 'Europe',
        sub: 'London · Paris · Berlin',
        country: 'United Kingdom', state: '',
        path: 'M 10,10 L 26,8 L 30,14 L 28,22 L 20,26 L 10,24 L 6,16 Z',
        color: '#378ADD',
    },
    {
        key: 'asia_east',
        label: 'Asia Pacific',
        sub: 'Tokyo · Singapore · Sydney',
        country: 'Japan', state: '',
        path: 'M 14,8 L 28,6 L 32,12 L 30,20 L 22,26 L 12,22 L 8,14 Z',
        color: '#FF9A3C',
    },
    {
        key: 'latam',
        label: 'Latin America',
        sub: 'São Paulo · CDMX · Buenos Aires',
        country: 'Brazil', state: '',
        path: 'M 12,6 L 24,8 L 28,16 L 22,26 L 14,28 L 8,20 L 8,12 Z',
        color: '#FF6B6B',
    },
    {
        key: 'middle_east',
        label: 'Middle East',
        sub: 'Dubai · Tel Aviv · Riyadh',
        country: 'UAE', state: '',
        path: 'M 10,10 L 24,8 L 28,16 L 20,24 L 10,22 L 6,14 Z',
        color: '#FF12C7',
    },
    {
        key: 'africa',
        label: 'Africa',
        sub: 'Lagos · Nairobi · Cape Town',
        country: 'Nigeria', state: '',
        path: 'M 12,6 L 24,6 L 28,14 L 24,24 L 16,28 L 8,22 L 6,14 Z',
        color: '#00FFC0',
    },
]

// Tiny abstract shape SVG per region
function RegionShape({ path, color, active }: { path: string; color: string; active: boolean }) {
    return (
        <svg viewBox="0 0 40 32" style={{ width: 44, height: 36, flexShrink: 0 }} aria-hidden="true">
            <path
                d={path}
                fill={active ? `${color}22` : `${color}0d`}
                stroke={active ? color : `${color}55`}
                strokeWidth={active ? 1 : 0.6}
            />
            {active && (
                <circle cx="21" cy="16" r="2.5" fill={color} opacity="0.9" />
            )}
        </svg>
    )
}

export function WelcomeLocationStep({
    eyebrow, accentClass, btnClass, initialData, onNext,
}: WelcomeLocationStepProps) {
    const [selected, setSelected] = useState<string>(
        initialData?.state
            ? (REGIONS.find(r => r.state === initialData.state)?.key ?? 'us_east')
            : 'us_east'
    )

    const handleNext = () => {
        const region = REGIONS.find(r => r.key === selected) ?? REGIONS[0]
        onNext({ country: region.country, state: region.state, city: region.sub.split(' · ')[0] })
    }

    return (
        <>
            <div className="ob-body">
                <div className={`ob-eyebrow ${accentClass}`}>{eyebrow}</div>
                <h2 className="ob-heading">Where are you?</h2>
                <p className="ob-sub">Sets your geographic signal filter.</p>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 6,
                }}>
                    {REGIONS.map(region => {
                        const active = selected === region.key
                        return (
                            <button
                                key={region.key}
                                type="button"
                                onClick={() => setSelected(region.key)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '9px 11px', borderRadius: 9, cursor: 'pointer', textAlign: 'left',
                                    border: `0.5px solid ${active ? `${region.color}55` : 'var(--color-border-tertiary)'}`,
                                    background: active ? `${region.color}08` : 'var(--color-background-secondary)',
                                    transition: 'all 0.12s',
                                    position: 'relative', overflow: 'hidden',
                                }}
                            >
                                {active && (
                                    <div style={{
                                        position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                                        background: `linear-gradient(90deg,${region.color},transparent 70%)`,
                                    }} />
                                )}
                                <RegionShape path={region.path} color={region.color} active={active} />
                                <div style={{ minWidth: 0 }}>
                                    <div style={{
                                        fontSize: 12, fontWeight: 500,
                                        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                                    }}>{region.label}</div>
                                    <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 1 }}>
                                        {region.sub}
                                    </div>
                                </div>
                                {active && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#00FFC0" strokeWidth="2.5" style={{ marginLeft: 'auto', flexShrink: 0 }} aria-hidden="true">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                )}
                            </button>
                        )
                    })}
                </div>
            </div>

            <div className="ob-footer">
                <div />
                <button type="button" className={`ob-btn-next ${btnClass}`} onClick={handleNext}>
                    Continue <ArrowRight size={13} />
                </button>
            </div>
        </>
    )
}